<template>
  <simulador-card :title="$t('tools.capacityTitle')" :info="$t('tools.info')">
    <simulador-field
      id="capacidade-maxima"
      :label="$t('tools.capacidadeMaximaLabel')"
      type="number"
      :value="capacidadeMaxima"
      :min="1"
      :max="100"
      unit="%"
      @input="updateCapacidadeMaxima"
      @change="calcularNumeroAlcancavel"
    />

    <simulador-field
      id="numero-alcancavel"
      :label="$t('tools.numeroAlcancavelLabel')"
      :value="numeroAlcancavel"
      :formula="$t('tools.numeroAlcancavelFormula')"
      :is-result="true"
    />

    <simulador-field
      id="frequencia-mensal"
      :label="$t('tools.frequenciaMensalLabel')"
      type="number"
      :value="frequenciaMensal"
      :min="1"
      @input="updateFrequenciaMensal"
      @change="calcularFaturamentoPotencial"
    />

    <simulador-field
      id="ticket-medio"
      :label="$t('tools.ticketMedioLabel')"
      type="number"
      :value="ticketMedio"
      :step="0.01"
      unit="R$"
      unit-position="left"
      @input="updateTicketMedio"
      @change="calcularFaturamentoPotencial"
    />

    <simulador-field
      id="valor-por-cliente"
      :label="$t('tools.valorPorClienteLabel')"
      :value="valorPorCliente"
      unit="R$"
      unit-position="left"
      :formula="$t('tools.valorPorClienteFormula')"
      :is-result="true"
    />

    <simulador-field
      id="faturamento-potencial"
      :label="$t('tools.faturamentoPotencialLabel')"
      :value="faturamentoPotencial"
      unit="R$"
      unit-position="left"
      :formula="$t('tools.faturamentoPotencialFormula')"
      :is-result="true"
    />
  </simulador-card>
</template>

<script>
import SimuladorCard from "./SimuladorCard.vue";
import SimuladorField from "./SimuladorField.vue";

export default {
  name: "CapacidadeFaturamento",
  components: {
    SimuladorCard,
    SimuladorField
  },
  props: {
    value: {
      type: Object,
      default: () => ({
        capacidadeMaxima: 70,
        numeroAlcancavel: 3251,
        frequenciaMensal: 4,
        ticketMedio: 20.0,
        valorPorCliente: "80,00",
        faturamentoPotencial: "260.080,00"
      })
    },
    totalPessoas: {
      type: [Number, String],
      default: 4644
    }
  },
  computed: {
    capacidadeMaxima: {
      get() {
        return this.value.capacidadeMaxima;
      },
      set(val) {
        this.$emit("input", { ...this.value, capacidadeMaxima: val });
      }
    },
    numeroAlcancavel: {
      get() {
        return this.value.numeroAlcancavel;
      },
      set(val) {
        this.$emit("input", { ...this.value, numeroAlcancavel: val });
      }
    },
    frequenciaMensal: {
      get() {
        return this.value.frequenciaMensal;
      },
      set(val) {
        this.$emit("input", { ...this.value, frequenciaMensal: val });
      }
    },
    ticketMedio: {
      get() {
        return this.value.ticketMedio;
      },
      set(val) {
        this.$emit("input", { ...this.value, ticketMedio: val });
      }
    },
    valorPorCliente: {
      get() {
        return this.value.valorPorCliente;
      },
      set(val) {
        this.$emit("input", { ...this.value, valorPorCliente: val });
      }
    },
    faturamentoPotencial: {
      get() {
        return this.value.faturamentoPotencial;
      },
      set(val) {
        this.$emit("input", { ...this.value, faturamentoPotencial: val });
      }
    }
  },
  methods: {
    updateCapacidadeMaxima(val) {
      this.capacidadeMaxima = val;
      // Recalcular imediatamente após a atualização
      this.$nextTick(() => {
        this.calcularNumeroAlcancavel();
      });
    },
    updateFrequenciaMensal(val) {
      this.frequenciaMensal = val;
      // Recalcular imediatamente após a atualização
      this.$nextTick(() => {
        this.calcularValorPorCliente();
        this.calcularFaturamentoPotencial();
      });
    },
    updateTicketMedio(val) {
      this.ticketMedio = val;
      // Recalcular imediatamente após a atualização
      this.$nextTick(() => {
        this.calcularValorPorCliente();
        this.calcularFaturamentoPotencial();
      });
    },
    formatarMoeda(valor) {
      return valor.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    calcularNumeroAlcancavel() {
      // Converter os valores para números
      const totalPessoas = parseInt(this.totalPessoas) || 0;
      const capacidade = parseFloat(this.capacidadeMaxima) || 0;

      // Aplicar a porcentagem da capacidade máxima ao total de pessoas
      const alcancavel = Math.round((totalPessoas * capacidade) / 100);

      // Definir o valor diretamente no objeto de dados em vez de usar a propriedade computed
      this.$set(this.value, "numeroAlcancavel", alcancavel);

      // Também atualizar a propriedade local para garantir consistência
      this.$nextTick(() => {
        this.calcularFaturamentoPotencial();
      });
    },
    calcularValorPorCliente() {
      const frequencia = parseFloat(this.frequenciaMensal) || 0;
      const ticket = parseFloat(this.ticketMedio) || 0;
      const valorMensal = frequencia * ticket;

      // Formatar o valor
      const valorFormatado = this.formatarMoeda(valorMensal);

      // Definir o valor diretamente no objeto de dados
      this.$set(this.value, "valorPorCliente", valorFormatado);

      return valorMensal;
    },
    calcularFaturamentoPotencial() {
      // Obter valores atualizados do objeto value em vez das propriedades computed
      const alcancavel = parseInt(this.value.numeroAlcancavel) || 0;
      const frequencia = parseFloat(this.frequenciaMensal) || 0;
      const ticket = parseFloat(this.ticketMedio) || 0;

      // Fórmula: Número Alcançável × Frequência Mensal Estimado × Ticket Médio
      const faturamento = alcancavel * frequencia * ticket;

      // Formatar e atualizar o valor
      const valorFormatado = this.formatarMoeda(faturamento);

      // Atualizar diretamente no objeto de dados
      this.$set(this.value, "faturamentoPotencial", valorFormatado);

      // Emitir o valor numérico (não formatado) para uso em outros componentes
      this.$emit("faturamento-atualizado", faturamento);

      return faturamento;
    },
    // Método para forçar cálculo completo
    recalcularTudo() {
      this.calcularNumeroAlcancavel();
      this.calcularValorPorCliente();
      this.calcularFaturamentoPotencial();

      // Emitir o evento de faturamento atualizado para garantir que outros componentes sejam atualizados
      const alcancavel = parseInt(this.value.numeroAlcancavel) || 0;
      const frequencia = parseFloat(this.frequenciaMensal) || 0;
      const ticket = parseFloat(this.ticketMedio) || 0;
      const faturamento = alcancavel * frequencia * ticket;

      this.$emit("faturamento-atualizado", faturamento);

      return faturamento;
    }
  },
  watch: {
    totalPessoas: {
      handler(newVal) {
        this.calcularNumeroAlcancavel();
      },
      immediate: true
    },
    capacidadeMaxima: {
      handler(newVal) {
        this.calcularNumeroAlcancavel();
      },
      immediate: true
    },
    frequenciaMensal: {
      handler(newVal) {
        this.calcularValorPorCliente();
        this.calcularFaturamentoPotencial();
      },
      immediate: true
    },
    ticketMedio: {
      handler(newVal) {
        this.calcularValorPorCliente();
        this.calcularFaturamentoPotencial();
      },
      immediate: true
    }
  },
  mounted() {
    // Garantir que os cálculos sejam feitos após a montagem do componente
    this.$nextTick(() => {
      this.calcularNumeroAlcancavel();
      this.calcularValorPorCliente();
      this.calcularFaturamentoPotencial();
    });
  }
};
</script>
